import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Activity, FileClock, FilterX, Search, ShieldCheck, UserRound, X } from 'lucide-react';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import LoadingState from '../shared/LoadingState';
import './AuditLogs.css';

const operationLabels = {
  create: 'Cadastro',
  update: 'Alteração',
  delete: 'Exclusão'
};

const collectionLabels = {
  clients: 'Clientes',
  collaborators: 'Colaboradores',
  services: 'Serviços',
  reservations: 'Reservas',
  payments: 'Pagamentos',
  posProducts: 'Produtos do PDV',
  posSales: 'Vendas do PDV',
  cashSessions: 'Caixas do PDV',
  establishments: 'Pontos de venda',
  orders: 'Pedidos',
  companies: 'Empresa',
  businessExpenses: 'Despesas',
  collaboratorPayments: 'Repasses',
  'establishments/menuItems': 'Itens do cardápio'
};

const fieldLabels = {
  name: 'Nome',
  userName: 'Nome do usuário',
  clientName: 'Cliente',
  serviceName: 'Serviço',
  status: 'Status',
  active: 'Ativo',
  price: 'Preço',
  totalAmount: 'Valor total',
  paidAmount: 'Valor pago',
  paymentStatus: 'Status do pagamento',
  phone: 'Telefone',
  email: 'E-mail',
  userEmail: 'E-mail do usuário',
  permissions: 'Permissões',
  collaboratorTypes: 'Tipos de colaborador',
  establishmentIds: 'Pontos de venda',
  checkInStatus: 'Check-in',
  openingAmount: 'Valor de abertura',
  closingAmount: 'Valor de fechamento'
};

const dateValue = value => {
  if (!value) return null;
  const parsed = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = value => dateValue(value)?.toLocaleString('pt-BR') || '-';

const formatValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const AuditLogs = () => {
  const { userData } = useAuth();
  const companyId = userData?.companyId || userData?.currentCompanyId;
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filters, setFilters] = useState({ search: '', user: 'all', operation: 'all', collection: 'all', startDate: '', endDate: '' });

  useEffect(() => {
    if (!companyId) return undefined;
    const logsQuery = query(
      collection(db, `companies/${companyId}/auditLogs`),
      orderBy('timestamp', 'desc'),
      limit(500)
    );
    return onSnapshot(logsQuery, snapshot => {
      setLogs(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
      setLoading(false);
    }, error => {
      console.error('Erro ao carregar auditoria:', error);
      setLoading(false);
    });
  }, [companyId]);

  const users = useMemo(() => {
    const map = new Map();
    logs.forEach(log => {
      const key = log.actorId || log.actorEmail || 'unknown';
      if (!map.has(key)) map.set(key, log.actorName || log.actorEmail || 'Usuário não identificado');
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [logs]);

  const collections = useMemo(() => [...new Set(logs.map(log => log.collectionName).filter(Boolean))].sort(), [logs]);

  const filteredLogs = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const start = filters.startDate ? new Date(`${filters.startDate}T00:00:00`) : null;
    const end = filters.endDate ? new Date(`${filters.endDate}T23:59:59.999`) : null;
    return logs.filter(log => {
      const timestamp = dateValue(log.timestamp);
      const actorKey = log.actorId || log.actorEmail || 'unknown';
      const searchable = [
        log.actorName, log.actorEmail, log.documentName, log.documentId,
        log.collectionName, ...(log.changedFields || [])
      ].filter(Boolean).join(' ').toLowerCase();
      return (!term || searchable.includes(term)) &&
        (filters.user === 'all' || actorKey === filters.user) &&
        (filters.operation === 'all' || log.operation === filters.operation) &&
        (filters.collection === 'all' || log.collectionName === filters.collection) &&
        (!start || (timestamp && timestamp >= start)) &&
        (!end || (timestamp && timestamp <= end));
    });
  }, [filters, logs]);

  const clearFilters = () => setFilters({ search: '', user: 'all', operation: 'all', collection: 'all', startDate: '', endDate: '' });

  if (loading) return <LoadingState title="Carregando auditoria" subtitle="Buscando alterações da empresa" />;

  return <div className="audit-container">
    <header className="audit-header">
      <div><span>Controle administrativo</span><h1>Auditoria</h1><p>Cadastros, alterações e exclusões realizadas no sistema.</p></div>
      <ShieldCheck size={34} />
    </header>

    <section className="audit-summary">
      <div><FileClock /><span>Eventos carregados</span><strong>{logs.length}</strong></div>
      <div><Activity /><span>Resultados do filtro</span><strong>{filteredLogs.length}</strong></div>
      <div><UserRound /><span>Usuários identificados</span><strong>{users.filter(([key]) => key !== 'unknown').length}</strong></div>
    </section>

    <section className="audit-filters">
      <label className="audit-search"><Search size={18} /><input value={filters.search} onChange={event => setFilters({ ...filters, search: event.target.value })} placeholder="Buscar usuário, cadastro, campo ou código" /></label>
      <select value={filters.user} onChange={event => setFilters({ ...filters, user: event.target.value })} aria-label="Filtrar por usuário">
        <option value="all">Todos os usuários</option>
        {users.map(([key, name]) => <option key={key} value={key}>{name}</option>)}
      </select>
      <select value={filters.operation} onChange={event => setFilters({ ...filters, operation: event.target.value })} aria-label="Filtrar por operação">
        <option value="all">Todas as operações</option><option value="create">Cadastros</option><option value="update">Alterações</option><option value="delete">Exclusões</option>
      </select>
      <select value={filters.collection} onChange={event => setFilters({ ...filters, collection: event.target.value })} aria-label="Filtrar por área">
        <option value="all">Todas as áreas</option>
        {collections.map(name => <option key={name} value={name}>{collectionLabels[name] || name}</option>)}
      </select>
      <input type="date" value={filters.startDate} onChange={event => setFilters({ ...filters, startDate: event.target.value })} aria-label="Data inicial" />
      <input type="date" value={filters.endDate} onChange={event => setFilters({ ...filters, endDate: event.target.value })} aria-label="Data final" />
      <button type="button" onClick={clearFilters} title="Limpar filtros"><FilterX size={18} /></button>
    </section>

    <section className="audit-table-wrap">
      {filteredLogs.length === 0 ? <p className="audit-empty">Nenhum evento encontrado para os filtros selecionados.</p> :
        <table className="audit-table"><thead><tr><th>Data/Hora</th><th>Usuário</th><th>Ação</th><th>Área</th><th>Registro</th><th>Campos</th></tr></thead>
          <tbody>{filteredLogs.map(log => <tr key={log.id} onClick={() => setSelectedLog(log)} tabIndex="0">
            <td data-label="Data/Hora">{formatDateTime(log.timestamp)}</td>
            <td data-label="Usuário"><strong>{log.actorName || log.actorEmail || 'Não identificado'}</strong><small>{log.actorEmail || log.actorId || '-'}</small></td>
            <td data-label="Ação"><span className={`audit-operation is-${log.operation}`}>{operationLabels[log.operation] || log.operation}</span></td>
            <td data-label="Área">{collectionLabels[log.collectionName] || log.collectionName}</td>
            <td data-label="Registro"><strong>{log.documentName || log.documentId}</strong><small>{log.documentId}</small></td>
            <td data-label="Campos">{(log.changedFields || []).map(field => fieldLabels[field] || field).join(', ') || '-'}</td>
          </tr>)}</tbody>
        </table>}
    </section>

    {selectedLog && <div className="audit-overlay" onClick={() => setSelectedLog(null)}>
      <div className="audit-modal" onClick={event => event.stopPropagation()}>
        <header><div><span>{operationLabels[selectedLog.operation] || selectedLog.operation}</span><h2>{collectionLabels[selectedLog.collectionName] || selectedLog.collectionName}</h2><p>{selectedLog.documentName || selectedLog.documentId}</p></div><button type="button" onClick={() => setSelectedLog(null)} title="Fechar"><X /></button></header>
        <dl className="audit-metadata"><div><dt>Data</dt><dd>{formatDateTime(selectedLog.timestamp)}</dd></div><div><dt>Usuário</dt><dd>{selectedLog.actorName || 'Não identificado'}<small>{selectedLog.actorEmail || selectedLog.actorId || '-'}</small></dd></div><div><dt>Documento</dt><dd>{selectedLog.documentPath}</dd></div></dl>
        <div className="audit-changes"><h3>Campos alterados</h3>{(selectedLog.changes || []).length === 0 ? <p>Nenhum detalhe disponível.</p> : selectedLog.changes.map(change => <article key={change.field}><strong>{fieldLabels[change.field] || change.field}</strong><div><span>Antes</span><pre>{formatValue(change.before)}</pre></div><div><span>Depois</span><pre>{formatValue(change.after)}</pre></div></article>)}</div>
      </div>
    </div>}
  </div>;
};

export default AuditLogs;
